export function generateLPReportHTML(jsonData) {
    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return new Date().toLocaleString('pt-BR');
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR');
    };

    // Helper function to generate table HTML
    const generateTable = (step) => {
        const rows = step.rows || [];
        const columns = step.columns || [];
        
        let tableHTML = `
            <table class="compact-table">
                <thead>
                    <tr>`;
        
        // Add headers
        columns.forEach(col => {
            tableHTML += `<th>${col}</th>`;
        });
        
        tableHTML += `
                    </tr>
                </thead>
                <tbody>`;
        
        // Add rows
        rows.forEach(row => {
            tableHTML += `<tr>`;
            
            // First cell is the row label
            const rowLabel = row.includes(':') ? row.split(':')[0] + ':' : row;
            tableHTML += `<td class="row-label">${rowLabel}</td>`;
            
            // Add empty cells for each column except the first
            for (let i = 1; i < columns.length; i++) {
                tableHTML += `<td></td>`;
            }
            
            tableHTML += `</tr>`;
        });
        
        tableHTML += `
                </tbody>
            </table>`;
        
        return tableHTML;
    };

    // Helper function to generate camera step HTML
    const generateCameraStep = (step) => {
        let cameraHTML = '';
        
        if (step.subSteps && step.subSteps.length > 0) {
            step.subSteps.forEach(subStep => {
                cameraHTML += `
                    <div class="camera-step">
                        <div class="camera-title">📷 ${subStep.title}</div>
                        <div class="camera-hints">${subStep.hints ? subStep.hints.join(', ') : ''}</div>
                    </div>`;
            });
        } else {
            cameraHTML = `
                <div class="camera-step">
                    <div class="camera-title">📸 ${step.title}</div>
                    <div class="camera-hints">Capture de imagem</div>
                </div>`;
        }
        
        return cameraHTML;
    };

    // Helper function to generate form step HTML
    const generateFormStep = (step) => {
        let formHTML = '';
        
        if (step.fields && step.fields.length > 0) {
            step.fields.forEach(field => {
                formHTML += `<div class="form-field">`;
                
                // Add label
                formHTML += `<div class="field-label">${field.label}${field.isRequired ? ' *' : ''}</div>`;
                
                if (field.type === 'select') {
                    // Generate select dropdown
                    formHTML += `<select class="field-input" ${field.isDisabled ? 'disabled' : ''} ${field.typeVariant === 'multiple' ? 'multiple' : ''}>`;
                    formHTML += `<option value="">Selecione...</option>`;
                    if (field.options) {
                        field.options.forEach(option => {
                            formHTML += `<option value="${option}">${option}</option>`;
                        });
                    }
                    formHTML += `</select>`;
                } else if (field.type === 'input') {
                    // Generate input field
                    formHTML += `<input type="text" class="field-input" placeholder="${field.label}" ${field.isDisabled ? 'disabled' : ''} value="${field.defaultValue || ''}" />`;
                }
                
                formHTML += `</div>`;
            });
        }
        
        return formHTML;
    };

    // Generate steps HTML
    const generateSteps = (steps) => {
        let stepsHTML = '';
        let stepNumber = 1;
        
        // Check if steps 3 and 4 are camera steps to group them
        const shouldGroupCameraSteps = steps.length >= 4 && 
            steps[2].stepType === 'camera' && 
            steps[3].stepType === 'camera';
        
        steps.forEach((step, index) => {
            // Skip if this is the second camera step in a group
            if (shouldGroupCameraSteps && index === 3) {
                return;
            }
            
            // Start inline group for camera steps
            if (shouldGroupCameraSteps && index === 2) {
                stepsHTML += '<div class="inline-step">';
            }
            
            stepsHTML += `
            <div class="step">
                <div class="step-header">
                    <div class="step-title">${stepNumber}. ${step.title}</div>
                    <div class="step-badges">
                        <span class="type-badge">${step.stepType === 'table' ? 'Tabela' : step.stepType === 'form' ? 'Formulário' : 'Câmera'}</span>
                        ${step.isRequired ? '<span class="required-badge">Obrigatório</span>' : ''}
                    </div>
                </div>
                <div class="step-content">`;
            
            if (step.stepType === 'table') {
                stepsHTML += generateTable(step);
            } else if (step.stepType === 'camera') {
                stepsHTML += generateCameraStep(step);
            } else if (step.stepType === 'form') {
                stepsHTML += generateFormStep(step);
            }
            
            stepsHTML += `
                </div>
            </div>`;
            
            // Add the second camera step if grouping
            if (shouldGroupCameraSteps && index === 2) {
                const nextStep = steps[3];
                stepNumber++;
                stepsHTML += `
            <div class="step">
                <div class="step-header">
                    <div class="step-title">${stepNumber}. ${nextStep.title}</div>
                    <div class="step-badges">
                        <span class="type-badge">${nextStep.stepType === 'table' ? 'Tabela' : nextStep.stepType === 'form' ? 'Formulário' : 'Câmera'}</span>
                        ${nextStep.isRequired ? '<span class="required-badge">Obrigatório</span>' : ''}
                    </div>
                </div>
                <div class="step-content">
                    ${nextStep.stepType === 'table' ? generateTable(nextStep) : nextStep.stepType === 'form' ? generateFormStep(nextStep) : generateCameraStep(nextStep)}
                </div>
            </div>`;
                
                // Close inline group
                stepsHTML += '</div>';
            }
            
            stepNumber++;
        });
        
        return stepsHTML;
    };

    // Generate the complete HTML
    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${jsonData.title} - Relatório</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        @page {
            size: A4;
            margin: 15mm;
        }

        body {
            font-family: Arial, sans-serif;
            font-size: 11px;
            line-height: 1.3;
            color: #333;
            background: white;
            max-width: 210mm;
            margin: 0 auto;
            padding: 10px;
        }

        .container {
            background: white;
            border: 2px solid #184d7b;
        }

        .header {
            background: linear-gradient(135deg, #184d7b, #184d7b);
            color: white;
            padding: 12px;
            text-align: center;
        }

        .header h1 {
            font-size: 18px;
            margin-bottom: 4px;
            font-weight: bold;
        }

        .header .subtitle {
            font-size: 12px;
            font-weight: normal;
        }

        .info-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            padding: 10px;
            background: #f8f9fa;
            border-bottom: 1px solid #ddd;
        }

        .info-box {
            background: white;
            padding: 8px;
            border-radius: 4px;
            border-left: 3px solid #184d7b;
            font-size: 10px;
        }

        .info-box h4 {
            color: #184d7b;
            font-size: 11px;
            margin-bottom: 4px;
            font-weight: bold;
        }

        .info-box p {
            margin-bottom: 2px;
        }

        .instruments {
            display: inline-block;
        }

        .instrument {
            background: #e3f2fd;
            color: #1565c0;
            padding: 2px 6px;
            border-radius: 8px;
            font-size: 9px;
            margin-right: 4px;
            font-weight: 500;
        }

        .annotations {
            padding: 8px;
            background: #fff3e0;
            border-bottom: 1px solid #ddd;
        }

        .annotations h4 {
            color: #184d7b;
            font-size: 11px;
            margin-bottom: 4px;
            font-weight: bold;
        }

        .annotation {
            font-size: 9px;
            line-height: 1.2;
            margin-bottom: 4px;
            text-align: justify;
        }

        .steps-section {
            padding: 10px;
        }

        .steps-title {
            background: #184d7b;
            color: white;
            padding: 6px;
            text-align: center;
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 8px;
        }

        .step {
            margin-bottom: 12px;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            overflow: hidden;
        }

        .step-header {
            background: #f5f5f5;
            padding: 6px 8px;
            border-bottom: 1px solid #ddd;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .step-title {
            font-size: 11px;
            font-weight: bold;
            color: #333;
        }

        .step-badges {
            display: flex;
            gap: 4px;
        }

        .badge {
            padding: 2px 6px;
            border-radius: 8px;
            font-size: 8px;
            font-weight: bold;
            text-transform: uppercase;
        }

        .required-badge {
            color: #ff5722;
        }

        .type-badge {
            color: #2196f3;
        }

        .step-content {
            padding: 6px;
        }

        .compact-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9px;
            margin-bottom: 4px;
        }

        .compact-table th {
            background: #184d7b;
            color: white;
            padding: 4px;
            text-align: left;
            font-weight: bold;
            font-size: 9px;
        }

        .compact-table td {
            padding: 3px 4px;
            border: 1px solid #ddd;
            vertical-align: top;
        }

        .compact-table tr:nth-child(even) {
            background: #f8f9fa;
        }

        .row-label {
            font-weight: bold;
            color: #184d7b;
            background: #fff3e0 !important;
            font-size: 8px;
        }

        .camera-step {
            background: #e8f5e8;
            padding: 6px;
            border: 1px dashed #4CAF50;
            border-radius: 4px;
            text-align: center;
            margin: 4px 0;
        }

        .camera-title {
            font-size: 10px;
            color: #2e7d32;
            font-weight: bold;
            margin-bottom: 2px;
        }

        .camera-hints {
            color: #558b2f;
            font-size: 8px;
            font-style: italic;
        }

        .form-field {
            margin-bottom: 8px;
            background: #f8f9fa;
            padding: 6px;
            border-radius: 4px;
            border-left: 3px solid #4CAF50;
        }

        .field-label {
            font-size: 10px;
            font-weight: bold;
            color: #2e7d32;
            margin-bottom: 4px;
        }

        .field-input {
            width: 100%;
            padding: 4px 6px;
            border: 1px solid #ddd;
            border-radius: 3px;
            font-size: 9px;
            background: white;
        }

        .field-input:disabled {
            background: #f5f5f5;
            color: #666;
        }

        .field-input[multiple] {
            height: 60px;
        }

        .two-column-steps {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
        }

        .metadata {
            background: #37474f;
            color: white;
            padding: 6px;
            text-align: center;
            font-size: 9px;
        }

        .inline-step {
            display: flex;
            gap: 8px;
        }

        .inline-step .step {
            flex: 1;
            margin-bottom: 8px;
        }

        @media print {
            body {
                padding: 0;
                max-width: none;
            }
            
            .container {
                border: none;
            }
            
            * {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div style="display: flex; align-items: center; position: relative;">

                <div style="flex: 1; text-align: center;">
                    <h1>${jsonData.title.toUpperCase()}</h1>
                    <div class="subtitle">${jsonData.reportName || jsonData.title}</div>
                </div>
                
            </div>
        </div>

        <div class="info-section">
            <div class="info-box">
                <h4>Informações Gerais</h4>
                <p><strong>Código:</strong> ${jsonData.code} | <strong>Plataforma:</strong> ${jsonData.platform === 'mobile' ? 'Mobile' : jsonData.platform}</p>
                <p><strong>Tentativas:</strong> ${jsonData.allowedAttempts} | <strong>Critério:</strong> ${jsonData.testCriteria === 'continuity' ? 'Continuidade' : jsonData.testCriteria}</p>
                <p><strong>ID:</strong> ${jsonData._id?.$oid || jsonData._id || 'N/A'}</p>
                <p><strong>Data:</strong> ${formatDate(jsonData.createdAt?.$date || jsonData.createdAt)}</p>
            </div>
            <div class="info-box">
                <h4>Instrumentos e Propriedades</h4>
                <p><strong>Instrumentos:</strong> 
                    ${jsonData.instrumentIds.map(instrument => 
                        `<span class="instrument">${instrument === 'camera' ? 'Câmera' : 
                        instrument === 'oscilloscope' ? 'Osciloscópio' : instrument}</span>`
                    ).join('')}
                </p>
                <p><strong>Propriedades:</strong> ${jsonData.assetDisplayProperties.map(prop => 
                    prop === 'measurement_range' ? 'Faixa de Medição' : prop
                ).join(', ')}</p>
            </div>
        </div>

        ${jsonData.annotations && jsonData.annotations.length > 0 ? `
        <div class="annotations">
            <h4>Legendas e Observações Técnicas</h4>
            ${jsonData.annotations.map((annotation, index) => `
            <div class="annotation">
                <strong>${index === 0 ? 'Legenda:' : 'Obs:'}</strong> ${annotation}
            </div>
            `).join('')}
        </div>
        ` : ''}

        <div class="steps-section">
            <div class="steps-title">ETAPAS DO ENSAIO</div>
            ${generateSteps(jsonData.steps)}
        </div>

        <div class="metadata">
            Powered by Coodex <strong>Meris</strong> | <strong>Relatório:</strong> ${new Date().toLocaleDateString('pt-BR')} | <strong>Método:</strong> ${jsonData.title}
        </div>
    </div>

    <script>
        // Add interactive functionality for table cells
        document.addEventListener('DOMContentLoaded', function() {
            const tableCells = document.querySelectorAll('.compact-table td:not(.row-label)');
            tableCells.forEach(cell => {
                cell.addEventListener('click', function() {
                    if (this.querySelector('input')) return;
                    
                    const currentText = this.textContent;
                    const input = document.createElement('input');
                    input.type = 'text';
                    input.value = currentText;
                    input.style.border = 'none';
                    input.style.background = 'transparent';
                    input.style.width = '100%';
                    input.style.fontSize = '9px';
                    input.style.padding = '2px';
                    
                    this.innerHTML = '';
                    this.appendChild(input);
                    input.focus();
                    
                    input.addEventListener('blur', function() {
                        cell.textContent = this.value;
                    });
                    
                    input.addEventListener('keypress', function(e) {
                        if (e.key === 'Enter') {
                            this.blur();
                        }
                    });
                });
            });
        });
    </script>
</body>
</html>`;

    return html;
}